import React, { useState, useEffect } from 'react';
import { Typography, Card, Table, Button, Space, Select, Modal, Form, Input, message, Empty, Tag } from 'antd';
import { PlusOutlined, PlayCircleOutlined, CheckCircleOutlined, StopOutlined, PauseCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { testRunService } from '../services/testRunService';
import projectService from '../services/projectService';
import { TestRun, TestRunStatus, TestRunCreate } from '../types/testRun';
import { Project } from '../types/project';
import { User } from '../types/user';

const { Title } = Typography;
const { Option } = Select;

const TestRunsPage: React.FC = () => {
  const { t } = useTranslation();
    const navigate = useNavigate();
    const [testRuns, setTestRuns] = useState<TestRun[]>([]);
    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState(false);
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [filters, setFilters] = useState({
        project_id: undefined as number | undefined,
        status: undefined as TestRunStatus | undefined
    });

    useEffect(() => {
        fetchTestRuns();
        fetchProjects();
    }, [filters]);

    const fetchTestRuns = async () => {
        try {
            setLoading(true);
            if (!filters.project_id) {
                message.warning(t('testRuns.selectProject'));
                setTestRuns([]);
                return;
            }
            const data = await testRunService.getTestRuns(filters);
            setTestRuns(data);
        } catch (error) {
            message.error(t('testRuns.fetchError'));
        } finally {
            setLoading(false);
        }
    };

    const fetchProjects = async () => {
        try {
            const data = await projectService.getUserProjects();
            setProjects(data);
        } catch (error) {
            message.error(t('projects.fetchError'));
        }
    };

    const handleCreate = async (values: TestRunCreate) => {
        try {
            await testRunService.createTestRun(values);
            message.success(t('testRuns.createSuccess'));
            setCreateModalVisible(false);
            form.resetFields();
            fetchTestRuns();
        } catch (error) {
            message.error(t('testRuns.createError'));
        }
    };

    const handleStart = async (id: number) => {
        try {
            await testRunService.startTestRun(id);
            message.success(t('testRuns.startSuccess'));
            fetchTestRuns();
        } catch (error) {
            message.error(t('testRuns.startError'));
        }
    };

    const handleComplete = async (id: number) => {
        try {
            await testRunService.completeTestRun(id);
            message.success(t('testRuns.completeSuccess'));
            fetchTestRuns();
        } catch (error) {
            message.error(t('testRuns.completeError'));
        }
    };

    const handlePause = async (id: number) => {
        try {
            await testRunService.pauseTestRun(id);
            message.success(t('testRuns.pauseSuccess'));
            fetchTestRuns();
        } catch (error) {
            message.error(t('testRuns.pauseError'));
        }
    };

    const handleCancel = async (id: number) => {
        try {
            await testRunService.cancelTestRun(id);
            message.success(t('testRuns.cancelSuccess'));
            fetchTestRuns();
        } catch (error) {
            message.error(t('testRuns.cancelError'));
        }
    };

    const getStatusColor = (status: TestRunStatus) => {
        switch (status) {
            case TestRunStatus.DRAFT:
                return 'default';
            case TestRunStatus.IN_PROGRESS:
                return 'processing';
            case TestRunStatus.COMPLETED:
                return 'success';
            case TestRunStatus.BLOCKED:
                return 'warning';
            case TestRunStatus.CANCELLED:
                return 'error';
            default:
                return 'default';
        }
    };

    const columns = [
        {
            title: t('testRuns.name'),
            dataIndex: 'name',
            key: 'name',
            render: (text: string, record: TestRun) => (
                <a onClick={() => navigate(`/test-runs/${record.id}`)}>{text}</a>
            )
        },
        {
            title: t('testRuns.project'),
            dataIndex: 'project_name',
            key: 'project_name'
        },
        {
            title: t('testRuns.status'),
            dataIndex: 'status',
            key: 'status',
            render: (status: TestRunStatus) => (
                <Tag color={getStatusColor(status)}>
                    {t(`testRuns.status.${status}`)}
                </Tag>
            )
        },
        {
            title: t('testRuns.assignedTo'),
            dataIndex: 'assigned_to',
            key: 'assigned_to',
            render: (assigned_to: User | undefined) => {
                if (!assigned_to) return '-';
                return `${assigned_to.first_name || ''} ${assigned_to.last_name || ''}`.trim() || assigned_to.email;
            }
        },
        {
            title: t('testRuns.environment'),
            dataIndex: 'environment',
            key: 'environment'
        },
        {
            title: t('testRuns.actions'),
            key: 'actions',
            render: (_: any, record: TestRun) => (
                <Space>
                    {record.status === TestRunStatus.DRAFT && (
                        <Button
                            type="primary"
                            icon={<PlayCircleOutlined />}
                            onClick={() => handleStart(record.id)}
                        >
                            {t('testRuns.start')}
                        </Button>
                    )}
                    {record.status === TestRunStatus.IN_PROGRESS && (
                        <>
                            <Button
                                type="primary"
                                icon={<CheckCircleOutlined />}
                                onClick={() => handleComplete(record.id)}
                            >
                                {t('testRuns.complete')}
                            </Button>
                            <Button
                                type="default"
                                icon={<PauseCircleOutlined />}
                                onClick={() => handlePause(record.id)}
                            >
                                {t('testRuns.pause')}
                            </Button>
                            <Button
                                danger
                                icon={<CloseCircleOutlined />}
                                onClick={() => handleCancel(record.id)}
                            >
                                {t('testRuns.cancel')}
                            </Button>
                        </>
                    )}
                    {record.status === TestRunStatus.BLOCKED && (
                        <>
                            <Button
                                type="primary"
                                icon={<PlayCircleOutlined />}
                                onClick={() => handleStart(record.id)}
                            >
                                {t('testRuns.resume')}
                            </Button>
                            <Button
                                danger
                                icon={<CloseCircleOutlined />}
                                onClick={() => handleCancel(record.id)}
                            >
                                {t('testRuns.cancel')}
                            </Button>
                        </>
                    )}
                </Space>
            )
        }
    ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
        <Title level={2}>{t('testRuns.title')}</Title>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setCreateModalVisible(true)}
                    >
                        {t('testRuns.create')}
                    </Button>
                </div>

                <div style={{ marginBottom: '16px' }}>
                    <Space>
                        <Select
                            style={{ width: 200 }}
                            placeholder={t('testRuns.filterByProject')}
                            allowClear
                            onChange={(value) => setFilters({ ...filters, project_id: value })}
                        >
                            {projects.map(project => (
                                <Option key={project.id} value={project.id}>{project.name}</Option>
                            ))}
                        </Select>
                        <Select
                            style={{ width: 200 }}
                            placeholder={t('testRuns.filterByStatus')}
                            allowClear
                            onChange={(value) => setFilters({ ...filters, status: value })}
                        >
                            {Object.values(TestRunStatus).map(status => (
                                <Option key={status} value={status}>{t(`testRuns.status.${status}`)}</Option>
                            ))}
                        </Select>
                    </Space>
                </div>

                <Table
                    columns={columns}
                    dataSource={testRuns}
                    rowKey="id"
                    loading={loading}
                    locale={{
                        emptyText: filters.project_id ? (
                            <Empty
                                description={t('testRuns.noTestRuns')}
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                        ) : (
                            <Empty
                                description={t('testRuns.selectProject')}
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                        )
                    }}
                />
      </Card>

            <Modal
                title={t('testRuns.create')}
                open={createModalVisible}
                onCancel={() => setCreateModalVisible(false)}
                footer={null}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleCreate}
                >
                    <Form.Item
                        name="name"
                        label={t('testRuns.name')}
                        rules={[{ required: true, message: t('testRuns.nameRequired') }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="description"
                        label={t('testRuns.description')}
                    >
                        <Input.TextArea />
                    </Form.Item>
                    <Form.Item
                        name="project_id"
                        label={t('testRuns.project')}
                        rules={[{ required: true, message: t('testRuns.projectRequired') }]}
                    >
                        <Select>
                            {projects.map(project => (
                                <Option key={project.id} value={project.id}>{project.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="environment"
                        label={t('testRuns.environment')}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit">
                            {t('testRuns.create')}
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
    </div>
  );
};

export default TestRunsPage; 